/**
 * Local Storage Service
 * 
 * Implementation using browser localStorage for player data persistence.
 * Used as fallback when Firestore is unavailable.
 */

import { StorageService, PlayerStateData } from './StorageService';

const STORAGE_KEY = 'storyworld_player';

export class LocalStorageService implements StorageService {
  isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  async loadPlayerState(): Promise<PlayerStateData | null> {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        return {
          player: data.player || data, // Handle both old and new formats
          position: data.position || { q: 0, r: 0 },
          exploredHexes: data.exploredHexes || [],
          travelState: data.travelState || null,
          // Sync Fields
          combatState: data.combatState || null,
          pendingEncounter: data.pendingEncounter || null,
          currentStory: data.currentStory || null,
          activeQuestId: data.activeQuestId || null,
          questState: data.questState || null,
        };
      }
    } catch (error) {
      console.error('Failed to load player from localStorage:', error);
    }
    return null;
  }

  async savePlayerState(state: PlayerStateData): Promise<void> {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...state.player,
        position: state.position,
        exploredHexes: state.exploredHexes,
        travelState: state.travelState,
        // Sync Fields
        combatState: state.combatState,
        pendingEncounter: state.pendingEncounter,
        activeQuestId: state.activeQuestId,
        questState: state.questState,
      }));
    } catch (error) {
      console.error('Failed to save player to localStorage:', error);
      throw error;
    }
  }
}

